import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OENDisplayState } from "../../data/enums/oe-display-state.enum";
import { OEModelGetterService } from "../model/oe-model-getter.service";
import { OEInputModel } from "../../data/models/oe-input.model";
import { OECoordinatorService } from "../oe-coordinator.service";
import { OEValidatorService } from "../oe-validator.service";
import { ALLOWED_TO_REMOVE_LEG } from "../../data/mappings/oe-various.const";
import { ReturnStatement } from "@angular/compiler";
import { OELoggerService } from "./oe-logger.service";

@Injectable()
export class OEStrategyRenderService {
  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    protected oeLogger: OELoggerService,
    private getter: OEModelGetterService,
    private coordinator: OECoordinatorService,
    private validator: OEValidatorService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
  }
  /**********************************************************************/
  /* STRATEGY RENDER API - Used by strategy rendering plugin */

  /* Used by the strategy plugin to determine if particular leg is valid, and the strategy render can switch to another leg that the user is requesting */
  public validateOrderDataAndNotify(refId: string): boolean {
    if (this.getter.has(refId)) {
      // do the validation if the order is not in terminal state or mode is not modify info
      if (this.getter.isNotInTerminalState(refId) && !this.validationException(refId)) {
        let errors = new Array<OEInputModel>();

        // DUPLICATE, EDIT, MODIFY_INFO
        let modeLoad: boolean = this.getter.isOperationModeAmendMarkup(refId);
        let privatized: boolean = this.getter.isModificationPrivatized(refId);
        let dataValid = this.validator.validateOrderData(refId, errors, modeLoad, privatized); // ,this.getter.isOperationModeModify(refId)

        if (!dataValid) {
          if (
            this.getter.isOperationModeNew(refId) ||
            (this.getter.isOperationModeModify(refId) &&
              !this.getter.isSrvOrderState(refId, CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED) &&
              !this.getter.isSrvOrderState(refId, CubiqTS.OrderState.ACCEPTANCE_NEEDED))
          ) {
            this.coordinator.cmdUnlockInterfaceControls(refId);
          }
          setTimeout(() => {
            this.coordinator.cmdInvalidInput(refId, errors);
            this.oeLogger.outputInputControlErrors(refId, errors, "On CHANGE STRATEGY NODE");
          }, 100);
        }
        return dataValid;
      } else {
        return true;
      }
    } else {
      logger.out.error(
        `ERROR: _oeActions.validateOrderDataAndNotify(): Order with refId ${refId} does not exist!`,
      );
      return false;
    }
  }

  private validationException(refId: string): boolean {
    // case 1
    let exceptionCase1: boolean =
      this.getter.isOperationModeModifyInfo(refId) &&
      (this.getter.isSrvOrderState(refId, CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED) ||
        this.getter.isSrvOrderState(refId, CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED));
    return exceptionCase1;
  }

  private isRemovableState(refId: string): boolean {
    if (this.getter.has(refId)) {
      let orderState = this.getter.getSrvOrderState(refId);

      return orderState != null
        ? ALLOWED_TO_REMOVE_LEG.has(orderState)
        : // pre-submit
          true;
    }

    // non existant in model
    return false;
  }
  /**
   * This function is designed to indicate to the strategy render whether the remove button should be displayed
   * So here are the rules:
   * - allowed to remove in these states: PENDING_CANCELLATION, SAVED, ACTIVE, REJECTED, PENDING, TRIGGERED, MODIFY_ACCEPTANCE_NEEDED, EXPIRY_ACCEPTANCE_NEEDED, TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED, IN_MODIFICATION, (ERROR, PROCESSING)
   * - not allowed to remove for root.
   * - not allowed to remove is filled (partially of full).
   * - not allowed to remove modify issuer
   * - not allowed to remove is leg in terminal state
   * @param refId
   */

  public isStrategyNodeRemovalAllowed(refId: string): boolean {
    let exists = this.getter.has(refId);
    let notRoot = !this.getter.isRootNode(refId);
    let notFilled = this.getter.hasNotBeenFilled(refId);
    let isTheModifyIssuer = this.getter.isThisTheModifyIssuer(refId);
    let notTheModifyIssuer = !isTheModifyIssuer;
    let isRemovableState = this.isRemovableState(refId);
    let isPrimarySibling = this.getter.isThePrimarySibling(refId);
    // leg is the primary sibling relationship when the parent is in terminal state
    let hasSibling = this.getter.hasSiblings(refId);

    let isSiblingRemovableState = hasSibling
      ? this.isRemovableState(this.getter.getSiblingRefId(refId))
      : false;

    null;

    let ancestorIndex = 0;
    let isAncestorRemovableState: boolean = false;
    // recurse parent orders back to root order- but break on repeat or null
    const seenAncestor = new Set([]);
    // TODO establish sensible limit
    const ancestorLimit = 1000;
    let nextAncestorRefId = notRoot ? this.getter.getParentRef(refId) : null;
    let hasAncestor = nextAncestorRefId != null;
    while (
      isAncestorRemovableState === false &&
      nextAncestorRefId != null &&
      seenAncestor.has(nextAncestorRefId) == false
    ) {
      isAncestorRemovableState = this.isRemovableState(nextAncestorRefId);
      seenAncestor.add(nextAncestorRefId);

      nextAncestorRefId = this.getter.getParentRef(nextAncestorRefId);
      ancestorIndex++;
      if (ancestorIndex == ancestorLimit) {
        // fail in permissive fashion to allow server to validate removal
        isAncestorRemovableState = true;
      }
    }

    // deleting nodes not allowed in Modify Info, Monitor Execution and Amend Markup
    if (
      !this.getter.isOperationModeModifyInfo(refId) &&
      !this.getter.isOperationModeAmendMarkup(refId) &&
      !this.getter.isOperationModeMonitorExecution(refId)
    ) {
      if (exists && notRoot && notFilled && notTheModifyIssuer && !isPrimarySibling) {
        if (hasAncestor === false || (hasAncestor && isAncestorRemovableState)) {
          return isRemovableState;
        }
      } else if (
        exists &&
        notRoot &&
        notFilled &&
        isAncestorRemovableState &&
        ((isPrimarySibling && isSiblingRemovableState) || (isTheModifyIssuer && !isPrimarySibling))
      ) {
        return isRemovableState;
      }
    }
    return false;
  }

  public isSwitchStrategyAllowed(refId: string): boolean {
    if (this.getter.has(refId)) {
      const rootNodeId = this.getter.getRootRefIdForStrategyRefId(refId);
      let displayState: any = this.getter.getDisplayState(rootNodeId);
      // cant switch strategies if in modify_out acceptance
      if (
        displayState === OENDisplayState.SHOW_MODIFY_OUT_ACCEPTANCE ||
        displayState === OENDisplayState.SHOW_REJECTED ||
        displayState === OENDisplayState.SHOW_UNLOCKED ||
        displayState === OENDisplayState.SHOW_LOST_HIDDEN ||
        displayState === OENDisplayState.SHOW_ACCEPTANCE_ALLOWED ||
        displayState === OENDisplayState.SHOW_ACCEPTANCE_EXTERNAL ||
        displayState === OENDisplayState.SHOW_ACCEPTANCE_CALL
      ) {
        //	logger.out.info(`WARNING: _oeActions: Can not switch strategies. MODIFY_OUT_ACCEPTANCE response needed!`);
        return false;
      } else {
        return true;
      }
    } else {
      logger.out.error(
        `ERROR: _oeActions.isSwitchStrategyAllowed(): Order with refId ${refId} does not exist!`,
      );
      return false;
    }
  }

  public doesItHaveValidationErrors(refId: string): boolean {
    return this.getter.isNotInTerminalState(refId) && this.getter.hasValidationErrors(refId);
  }
}
